import { useCallback, useEffect, useState } from 'react'
import { Edit3, Info, Plus, Search, Trash2 } from 'lucide-react'
import type { AudioGroup } from '../../api'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, Pill, RowActions } from '../../components/ui'

type AudioGroupsProps = {
  groups: AudioGroup[]
  onCreate: () => void
  onDelete: (group: AudioGroup) => Promise<void>
  onEdit: (group: AudioGroup) => void
  onRegisterMobileCreateAction?: (action: (() => void) | null) => void
}

export const AudioGroups = (props: AudioGroupsProps) => {
  const [search, setSearch] = useState('')
  const [openInfoGroupId, setOpenInfoGroupId] = useState<string | null>(null)
  const registerMobileCreateAction = props.onRegisterMobileCreateAction
  const onCreate = props.onCreate
  const visibleGroups = props.groups.filter((group) => {
    const query = search.trim().toLowerCase()

    return !query
      || group.name.toLowerCase().includes(query)
      || (group.description ?? '').toLowerCase().includes(query)
  })
  const openCreateDialog = useCallback((): void => {
    onCreate()
  }, [onCreate])

  useEffect(() => {
    registerMobileCreateAction?.(openCreateDialog)

    return () => {
      registerMobileCreateAction?.(null)
    }
  }, [openCreateDialog, registerMobileCreateAction])

  useEffect(() => {
    if (!openInfoGroupId) {
      return undefined
    }

    const closeInfoPopover = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && target.closest('.group-actions-cell')) {
        return
      }

      setOpenInfoGroupId(null)
    }

    document.addEventListener('pointerdown', closeInfoPopover)

    return () => {
      document.removeEventListener('pointerdown', closeInfoPopover)
    }
  }, [openInfoGroupId])

  return (
    <>
      <div className="toolbar visual-toolbar">
        <Button className="toolbar-upload group-create-button" mobileFullWidth onClick={props.onCreate} variant="primary">
          <Plus size={16} /> Create audio group
        </Button>
        <div className="search-field">
          <Search size={16} />
          <input
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search audio groups..."
            value={search}
          />
        </div>
      </div>

      <DataTableSurface className="audio-groups-table-card" fullWidth mobileStack mobileVariant="entityList">
        <DataTableElement>
          <thead>
            <tr>
              <th>Name</th>
              <th>Tracks</th>
              <th>Channels</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map((group) => {
              const infoOpen = openInfoGroupId === group.id

              return (
              <tr key={group.id}>
                <td className="group-title-cell" data-label="">
                  <span className="group-title-line">
                    <strong>{group.name}</strong>
                  </span>
                  {group.description && <span>{group.description}</span>}
                </td>
                <td className="group-secondary-cell" data-label="Tracks"><Pill strong>{group.track_count}</Pill></td>
                <td className="group-secondary-cell" data-label="Channels"><Pill strong>{group.channel_count}</Pill></td>
                <td className="group-secondary-cell" data-label="Updated">{new Date(group.updated_at).toLocaleString()}</td>
                <td className="group-actions-cell" data-label="Actions">
                  <RowActions className="group-mobile-actions" mobileStack>
                    <IconButton
                      aria-expanded={infoOpen}
                      icon={<Info size={15} />}
                      label={`Group info for ${group.name}`}
                      onClick={() => setOpenInfoGroupId((current) => current === group.id ? null : group.id)}
                      title="Group info"
                    />
                  </RowActions>
                  <RowActions className="group-inline-actions" mobileStack>
                    <IconButton
                      icon={<Edit3 size={15} />}
                      label={`Edit ${group.name}`}
                      onClick={() => props.onEdit(group)}
                      title="Edit"
                    />
                    <IconButton
                      icon={<Trash2 size={15} />}
                      label={`Delete ${group.name}`}
                      onClick={() => void props.onDelete(group)}
                      title="Delete"
                      variant="danger"
                    />
                  </RowActions>
                  {infoOpen && (
                    <div className="track-info-popover group-info-popover" role="dialog" aria-label={`Details for ${group.name}`}>
                      <div>
                        <span>Tracks</span>
                        <strong>{group.track_count}</strong>
                      </div>
                      <div>
                        <span>Channels</span>
                        <strong>{group.channel_count}</strong>
                      </div>
                      <div className="track-info-actions">
                        <IconButton
                          icon={<Edit3 size={15} />}
                          label={`Edit ${group.name}`}
                          onClick={() => {
                            setOpenInfoGroupId(null)
                            props.onEdit(group)
                          }}
                          title="Edit"
                        />
                        <IconButton
                          icon={<Trash2 size={15} />}
                          label={`Delete ${group.name}`}
                          onClick={() => {
                            setOpenInfoGroupId(null)
                            void props.onDelete(group)
                          }}
                          title="Delete"
                          variant="danger"
                        />
                      </div>
                    </div>
                  )}
                </td>
              </tr>
              )
            })}
          </tbody>
        </DataTableElement>
        {visibleGroups.length === 0 && <EmptyState message="No audio groups match this search." />}
      </DataTableSurface>
    </>
  )
}
